import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X, Plus, Trash2 } from 'lucide-react';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';

import TextInput from '../../components/ui/TextInput';
import TimeInput from '../../components/ui/TimeInput';
import ColorPicker from '../../components/ui/ColorPicker';
import CheckboxInput from '../../components/ui/CheckboxInput';
import GenericSelect from '../../components/ui/GenericSelect';
import Button from '../../components/ui/Button';

const EMPTY_BREAK = { start: '', end: '' };

// AKM POC §5.7 — WorkHourKinds: 0 normal, 1 nöbet. 24 saatlik vardiyada başlangıç = bitiş meşrudur
// (vardiya ertesi gün aynı saatte biter); işaretsizken başlangıç = bitiş sunucuda 400
// workHour.startEqualsEnd. İstemci kapısı motorun kuralıyla AYNI: yalnız başlangıç = bitiş denetlenir,
// mola doğrulaması (workHour.invalidBreaks) sunucudadır.
export const KIND_NORMAL = 0;
export const KIND_DUTY = 1;

function timeSpanToHHmm(ts) {
  if (!ts) return '';
  if (typeof ts === 'string') {
    const parts = ts.split(':');
    return parts.length >= 2 ? `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}` : ts;
  }
  return '';
}

function hhmmToTimeSpan(val) {
  if (!val) return '00:00:00';
  const parts = val.split(':');
  return `${(parts[0] || '00').padStart(2, '0')}:${(parts[1] || '00').padStart(2, '0')}:00`;
}

export default function WorkHourDrawer({ open, onClose, initialValues, onSave }) {
  const { t } = useTranslation();

  const [name, setName] = useState('');
  const [shortCode, setShortCode] = useState('');
  const [colorCode, setColorCode] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [lateEntranceTolerance, setLateEntranceTolerance] = useState('');
  const [earlyLeaveTolerance, setEarlyLeaveTolerance] = useState('');
  const [breaks, setBreaks] = useState([]);
  const [is24Hours, setIs24Hours] = useState(false);
  const [kind, setKind] = useState(KIND_NORMAL);
  const [saving, setSaving] = useState(false);

  const kindOptions = [
    { id: KIND_NORMAL, name: t('workHour.kindNormal') },
    { id: KIND_DUTY, name: t('workHour.kindDuty') },
  ];

  // 24 saatlikte bitiş başlangıca KİLİTLİ: gösterilen ve gönderilen bitiş başlangıçtır.
  const effectiveEnd = is24Hours ? start : end;
  const startEqualsEnd = !is24Hours && !!start && !!end && start === end;

  useEffect(() => {
    if (open) {
      if (initialValues) {
        setName(initialValues.name || '');
        setShortCode(initialValues.shortCode || '');
        setColorCode(initialValues.colorCode || '');
        setStart(timeSpanToHHmm(initialValues.start));
        setEnd(timeSpanToHHmm(initialValues.end));
        setLateEntranceTolerance(initialValues.lateEntranceTolerance ?? '');
        setEarlyLeaveTolerance(initialValues.earlyLeaveTolerance ?? '');
        setBreaks((initialValues.breaks || []).map(b => ({
          start: timeSpanToHHmm(b.start),
          end: timeSpanToHHmm(b.end),
        })));
        setIs24Hours(!!initialValues.is24Hours);
        setKind(initialValues.kind === KIND_DUTY ? KIND_DUTY : KIND_NORMAL);
      } else {
        setName('');
        setShortCode('');
        setColorCode('');
        setStart('');
        setEnd('');
        setLateEntranceTolerance('');
        setEarlyLeaveTolerance('');
        setBreaks([]);
        setIs24Hours(false);
        setKind(KIND_NORMAL);
      }
    }
  }, [open, initialValues]);

  const handle24HoursChange = (checked) => {
    setIs24Hours(!!checked);
    // İşaretlenince bitiş başlangıca eşitlenir; işaret kalkınca bitiş boşalır (kullanıcı yeni bitişi
    // seçene dek kaydet kapalıdır — başlangıç = bitiş hatasını hemen göstermemek için).
    setEnd(checked ? start : '');
  };

  const handleAddBreak = () => setBreaks(prev => [...prev, { ...EMPTY_BREAK }]);

  const handleRemoveBreak = (idx) => setBreaks(prev => prev.filter((_, i) => i !== idx));

  const handleBreakChange = (idx, field, val) => {
    setBreaks(prev => prev.map((b, i) => i === idx ? { ...b, [field]: val } : b));
  };

  const canSubmit = !!name && !!start && !!effectiveEnd && !startEqualsEnd;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    const payload = {
      name,
      shortCode: shortCode || null,
      colorCode: colorCode || null,
      start: hhmmToTimeSpan(start),
      end: hhmmToTimeSpan(effectiveEnd),
      lateEntranceTolerance: parseInt(lateEntranceTolerance) || 0,
      earlyLeaveTolerance: parseInt(earlyLeaveTolerance) || 0,
      is24Hours,
      kind: kind === KIND_DUTY ? KIND_DUTY : KIND_NORMAL,
      breaks: breaks
        .filter(b => b.start && b.end)
        .map(b => ({ start: hhmmToTimeSpan(b.start), end: hhmmToTimeSpan(b.end) })),
    };
    const success = await onSave(payload);
    if (success) onClose();
    setSaving(false);
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      direction="right"
      size="45%"
      duration={150}
      overlayOpacity={0.2}
      style={{
        display: 'flex', flexDirection: 'column', maxWidth: '100vw', minWidth: 380,
        margin: '12px', borderRadius: '6px', height: 'calc(100vh - 24px)',
        boxShadow: '0 8px 32px rgba(0, 66, 66, 0.12)', zIndex: 1000,
      }}
    >
      <Header>
        <Title>{initialValues ? t('workHour.edit') : t('workHour.new')}</Title>
        <CloseBtn onClick={onClose}><X size={20} /></CloseBtn>
      </Header>

      <Body>
        <FormGrid>
          <FullWidth>
            <TextInput label={t('workHour.name')} value={name} onChange={e => setName(e.target.value)} required />
          </FullWidth>
          <TextInput label={t('workHour.shortCode')} value={shortCode} onChange={e => setShortCode(e.target.value)} />
          <ColorPicker label={t('workHour.colorCode')} value={colorCode} onChange={setColorCode} />
          <FullWidth>
            <GenericSelect
              label={t('workHour.kind')}
              options={kindOptions}
              value={kind}
              onChange={(v) => setKind(v === KIND_DUTY ? KIND_DUTY : KIND_NORMAL)}
              clearable={false}
            />
          </FullWidth>
          <FullWidth>
            <CheckboxInput label={t('workHour.is24Hours')} checked={is24Hours} onChange={handle24HoursChange} />
            {is24Hours && <Hint>{t('workHour.is24HoursHint')}</Hint>}
          </FullWidth>
          <TimeInput label={t('workHour.start')} value={start} onChange={setStart} required />
          {is24Hours ? (
            // Kilitli bitiş: başlangıçla aynı saat, düzenlenemez.
            <TextInput label={t('workHour.end')} value={effectiveEnd} onChange={() => {}} disabled readOnly required />
          ) : (
            <TimeInput
              label={t('workHour.end')}
              value={end}
              onChange={setEnd}
              required
              error={startEqualsEnd ? t('responseErrors.workHour.startEqualsEnd') : undefined}
            />
          )}
          <TextInput label={t('workHour.lateEntranceTolerance')} type="number" value={lateEntranceTolerance} onChange={e => setLateEntranceTolerance(e.target.value)} min="0" max="240" />
          <TextInput label={t('workHour.earlyLeaveTolerance')} type="number" value={earlyLeaveTolerance} onChange={e => setEarlyLeaveTolerance(e.target.value)} min="0" max="240" />
        </FormGrid>

        <BreakSection>
          <BreakHeader>
            <BreakTitle>{t('workHour.breaks')}</BreakTitle>
            <AddBreakBtn type="button" onClick={handleAddBreak}>
              <Plus size={14} /> {t('workHour.addBreak')}
            </AddBreakBtn>
          </BreakHeader>

          {breaks.length === 0 && <EmptyText>{t('workHour.noBreaks')}</EmptyText>}

          {breaks.map((b, idx) => (
            <BreakRow key={idx}>
              <TimeInput label={t('workHour.start')} value={b.start} onChange={v => handleBreakChange(idx, 'start', v)} required />
              <TimeInput label={t('workHour.end')} value={b.end} onChange={v => handleBreakChange(idx, 'end', v)} required />
              <RemoveBtn type="button" onClick={() => handleRemoveBreak(idx)}><Trash2 size={14} /></RemoveBtn>
            </BreakRow>
          ))}
        </BreakSection>
      </Body>

      <Footer>
        <Button variant="outline" onClick={onClose} disabled={saving}>{t('common.cancel')}</Button>
        <Button onClick={handleSubmit} disabled={saving || !canSubmit}>
          {saving ? '...' : t('common.save')}
        </Button>
      </Footer>
    </Drawer>
  );
}

const Header = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 20px 24px; border-bottom: 1px solid #E8ECF0;
`;
const Title = styled.h3`
  font-size: 16px; font-weight: 700; color: #004242; font-family: 'Nunito Sans', sans-serif; margin: 0;
`;
const CloseBtn = styled.button`
  display: flex; align-items: center; justify-content: center;
  width: 32px; height: 32px; border: none; border-radius: 4px;
  background: none; color: #A0A8B4; cursor: pointer;
  &:hover { background: #F5F7FA; }
`;
const Body = styled.div`
  flex: 1; min-height: 0; overflow-y: auto; padding: 24px;
  display: flex; flex-direction: column; gap: 20px;
`;
const Hint = styled.p`
  font-family: 'Nunito Sans', sans-serif; font-size: 11px; line-height: 1.45;
  color: #64748b; margin: 4px 0 0;
`;
const Footer = styled.div`
  display: flex; justify-content: flex-end; gap: 12px;
  padding: 16px 24px; border-top: 1px solid #E8ECF0;
`;
const FormGrid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 16px;
`;
const FullWidth = styled.div`
  grid-column: 1 / -1;
`;
const BreakSection = styled.div`
  border: 1.5px solid #E0E4E8; border-radius: 6px; padding: 16px;
`;
const BreakHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px;
`;
const BreakTitle = styled.span`
  font-family: 'Nunito Sans', sans-serif; font-size: 13px; font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;
const AddBreakBtn = styled.button`
  display: flex; align-items: center; gap: 4px;
  border: none; background: none;
  color: ${({ theme }) => theme.colors.primary.main};
  font-family: 'Nunito Sans', sans-serif; font-size: 12px; font-weight: 600;
  cursor: pointer; padding: 2px 0;
  &:hover { text-decoration: underline; }
`;
const EmptyText = styled.span`
  font-family: 'Nunito Sans', sans-serif; font-size: 12px; color: #A0A8B4;
`;
const BreakRow = styled.div`
  display: flex; align-items: flex-end; gap: 8px; margin-bottom: 8px;
`;
const RemoveBtn = styled.button`
  display: flex; align-items: center; justify-content: center;
  width: 36px; height: 36px; border: none; border-radius: 4px;
  background: none; color: #A0A8B4; cursor: pointer; flex-shrink: 0;
  &:hover { background: rgba(229, 57, 53, 0.08); color: #E53935; }
`;
